import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_OPTIONS = {
  initialLeftWidth: 550,
  leftMinWidth: 550,
  rightMinWidth: 200,
  rightMaxWidth: 400,
  handleWidth: 8,
  handleSpacing: 16,
  keyboardStep: 20,
};

const getLeftWidthBounds = (containerWidth, options) => {
  const { leftMinWidth, rightMinWidth, rightMaxWidth, handleWidth, handleSpacing } = options;

  const handleTotalWidth = handleWidth + handleSpacing;
  const availableWidth = Math.max(0, containerWidth - handleTotalWidth);
  const effectiveRightMaxWidth = typeof rightMaxWidth === "number" ? rightMaxWidth : Number.POSITIVE_INFINITY;
  const preferredMinLeftWidth = Math.max(leftMinWidth, availableWidth - effectiveRightMaxWidth);

  const minLeftWidth = Math.min(availableWidth, preferredMinLeftWidth);
  const maxLeftWidth = Math.max(minLeftWidth, Math.max(0, availableWidth - rightMinWidth));

  return {
    minLeftWidth,
    maxLeftWidth,
  };
};

const clampLeftWidth = (proposedLeftWidth, containerWidth, options) => {
  const { minLeftWidth, maxLeftWidth } = getLeftWidthBounds(containerWidth, options);
  return Math.min(Math.max(proposedLeftWidth, minLeftWidth), maxLeftWidth);
};

const usePanelResize = (customOptions = {}) => {
  const options = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...customOptions }),
    [
      customOptions.initialLeftWidth,
      customOptions.leftMinWidth,
      customOptions.rightMinWidth,
      customOptions.rightMaxWidth,
      customOptions.handleWidth,
      customOptions.handleSpacing,
      customOptions.keyboardStep,
    ],
  );

  const {
    initialLeftWidth,
    leftMinWidth,
    rightMinWidth,
    rightMaxWidth,
    handleWidth,
    handleSpacing,
    keyboardStep,
  } = options;

  const boundsOptions = useMemo(
    () => ({ leftMinWidth, rightMinWidth, rightMaxWidth, handleWidth, handleSpacing }),
    [leftMinWidth, rightMinWidth, rightMaxWidth, handleWidth, handleSpacing],
  );

  const handleTotalWidth = handleWidth + handleSpacing;

  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerElement, setContainerElement] = useState(null);
  const containerRefInternal = useRef(null);

  const setContainerRef = useCallback((node) => {
    containerRefInternal.current = node;
    setContainerElement(node);
  }, []);
  setContainerRef.current = containerRefInternal.current;

  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startLeftWidth: initialLeftWidth,
  });

  const getContainer = useCallback(
    () => containerElement || containerRefInternal.current || setContainerRef.current,
    [containerElement],
  );

  const resizePanelArea = useCallback(
    (event) => {
      const element = getContainer();
      if (!dragStateRef.current.isDragging || !element) return;

      const currentContainerWidth = element.clientWidth;
      if (!currentContainerWidth) return;

      setContainerWidth(currentContainerWidth);

      const deltaX = event.clientX - dragStateRef.current.startX;
      const nextLeftWidth = clampLeftWidth(
        dragStateRef.current.startLeftWidth + deltaX,
        currentContainerWidth,
        boundsOptions,
      );
      setLeftWidth(nextLeftWidth);
    },
    [boundsOptions, getContainer],
  );

  const stopResize = useCallback(() => {
    dragStateRef.current.isDragging = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", resizePanelArea);
    window.removeEventListener("mouseup", stopResize);
  }, [resizePanelArea]);

  const startResize = useCallback(
    (event) => {
      const element = getContainer();
      if (event.button !== 0 || !element) return;

      event.preventDefault();
      dragStateRef.current = {
        isDragging: true,
        startX: event.clientX,
        startLeftWidth: leftWidth,
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      window.addEventListener("mousemove", resizePanelArea);
      window.addEventListener("mouseup", stopResize);
    },
    [getContainer, leftWidth, resizePanelArea, stopResize],
  );

  const handleResizeKeyDown = useCallback(
    (event) => {
      const element = getContainer();
      if (!element) return;

      const isLeftArrow = event.key === "ArrowLeft";
      const isRightArrow = event.key === "ArrowRight";

      if (!isLeftArrow && !isRightArrow) return;

      event.preventDefault();

      const currentContainerWidth = element.clientWidth;
      const delta = isRightArrow ? keyboardStep : -keyboardStep;
      setLeftWidth((previousLeftWidth) =>
        clampLeftWidth(previousLeftWidth + delta, currentContainerWidth, boundsOptions),
      );
    },
    [boundsOptions, getContainer, keyboardStep],
  );

  useEffect(() => () => stopResize(), [stopResize]);

  useEffect(() => {
    const element = getContainer();
    if (!element) return () => {};

    const adjustForContainer = () => {
      const currentContainerWidth = element.clientWidth;
      if (!currentContainerWidth) return;

      setContainerWidth(currentContainerWidth);
      setLeftWidth((previousLeftWidth) =>
        clampLeftWidth(previousLeftWidth, currentContainerWidth, boundsOptions),
      );
    };

    adjustForContainer();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", adjustForContainer);
      return () => window.removeEventListener("resize", adjustForContainer);
    }

    const observer = new ResizeObserver(adjustForContainer);
    observer.observe(element);

    return () => observer.disconnect();
  }, [boundsOptions, containerElement, getContainer]);

  const element = getContainer();
  const currentContainerWidth = element?.clientWidth || containerWidth;
  const effectiveContainerWidth = Math.max(containerWidth, currentContainerWidth);

  return {
    containerRef: setContainerRef,
    leftWidth,
    rightWidth: Math.max(0, effectiveContainerWidth - handleTotalWidth - leftWidth),
    startResize,
    handleResizeKeyDown,
  };
};

export default usePanelResize;
